import React, { useState, useEffect } from 'react';
import InsuranceInfoModal from '../InsuranceInfoModal';

const PatientProfile = ({ patientId, viewerRole, viewerPermissions }) => {
  const [patient, setPatient] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [accessRequest, setAccessRequest] = useState(null);
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [insuranceData, setInsuranceData] = useState(null);

  // API functions for insurance management
  const fetchInsuranceInfo = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${backendUrl}/api/user/insurance`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInsuranceData(data);
      } else if (response.status === 404) {
        // No insurance info found, this is okay
        setInsuranceData(null);
      } else {
        console.error('Failed to fetch insurance info');
      }
    } catch (error) {
      console.error('Error fetching insurance info:', error);
    }
  };

  const saveInsuranceInfo = async (insuranceFormData) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${backendUrl}/api/user/insurance`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(insuranceFormData)
      });
      
      if (response.ok) {
        const data = await response.json();
        setInsuranceData(data);
        // Also update mock data for immediate UI update
        patientData.insurance_info = {
          insurance_company: data.insurance_company,
          insurance_number: data.insurance_number,
          policy_holder_name: data.policy_holder_name,
          group_number: data.group_number,
          issuance_date: data.issuance_date,
          expiration_date: data.expiration_date,
          copay: patientData.insurance_info.copay // Keep existing copay
        };
      } else {
        throw new Error('Failed to save insurance information');
      }
    } catch (error) {
      console.error('Error saving insurance info:', error);
      throw error;
    }
  };

  const handleEditInsurance = () => {
    setShowInsuranceModal(true);
  };

  // Load insurance data on component mount
  useEffect(() => {
    fetchInsuranceInfo();
  }, []);

  // Privacy levels based on HIPAA compliance
  const privacyLevels = {
    PUBLIC: ['name', 'age', 'gender'], // Basic info visible to all
    MEDICAL_STAFF: ['allergies', 'current_medications', 'emergency_contact'], // Healthcare providers
    AUTHORIZED_DOCTOR: ['medical_history', 'lab_results', 'diagnoses', 'treatments'], // Treating physicians
    PATIENT_ONLY: ['insurance_info', 'payment_methods', 'personal_notes'] // Patient only
  };

  // Mock patient data with privacy controls - Updated to reflect comprehensive healthcare platform
  const [patientData] = useState({
    id: patientId || 'patient-001',
    // PUBLIC information
    name: 'Demo User',
    age: 32,
    gender: 'Female',
    profile_image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NTMzODg3NjR8MA&ixlib=rb-4.1.0&q=85',
    
    // MEDICAL_STAFF information
    allergies: ['Penicillin', 'Shellfish', 'Latex'],
    current_medications: [
      { name: 'Lisinopril 10mg', frequency: 'Once daily', prescribed_by: 'Dr. Michael Chen' },
      { name: 'Metformin 500mg', frequency: 'Twice daily', prescribed_by: 'Dr. Sarah Kim' }
    ],
    emergency_contact: {
      name: 'John Johnson (Husband)',
      phone: '+965 9876 5432',
      relationship: 'Spouse'
    },
    
    // AUTHORIZED_DOCTOR information
    medical_history: [
      {
        condition: 'Type 2 Diabetes',
        diagnosed: '2019-03-15',
        status: 'Managed',
        treating_doctor: 'Dr. Sarah Kim'
      },
      {
        condition: 'Hypertension',
        diagnosed: '2020-07-22',
        status: 'Controlled',
        treating_doctor: 'Dr. Michael Chen'
      }
    ],
    lab_results: [
      {
        test: 'HbA1c',
        result: '6.8%',
        date: '2025-01-10',
        normal_range: '< 7.0%',
        status: 'Normal'
      },
      {
        test: 'Blood Pressure',
        result: '128/82 mmHg',
        date: '2025-01-10',
        normal_range: '< 130/80',
        status: 'Slightly Elevated'
      }
    ],
    diagnoses: [
      {
        primary: 'Type 2 Diabetes Mellitus',
        icd_code: 'E11.9',
        date: '2019-03-15',
        doctor: 'Dr. Sarah Kim'
      }
    ],
    treatments: [
      {
        treatment: 'Diabetes Management Program',
        start_date: '2019-03-20',
        status: 'Ongoing',
        provider: 'Gulf Medical Center'
      }
    ],
    
    // PATIENT_ONLY information
    insurance_info: {
      insurance_company: 'Kuwait Insurance Company',
      insurance_number: 'KIC-123456789',
      policy_holder_name: 'Demo User',
      group_number: 'GRP-001',
      issuance_date: '2023-01-01',
      expiration_date: '2025-12-31',
      copay: '15 KD'
    },
    payment_methods: [
      { type: 'Credit Card', last_four: '4532', default: true },
      { type: 'K-Net', account: 'KNET-789', default: false }
    ],
    personal_notes: 'Prefers morning appointments. Anxiety about blood draws. Speaks Arabic and English.',
    
    // Comprehensive visit history reflecting all provider types in EnayaHub platform
    recent_visits: [
      {
        date: '2025-01-28',
        provider: 'Dr. Sarah Al-Ahmad',
        provider_type: 'Doctor',
        category: 'Cardiology',
        type: 'Follow-up',
        diagnosis: 'Hypertension monitoring',
        notes: 'Blood pressure stable, continue medication',
        location: 'Dar Al Shifa Hospital',
        rating: 4.8
      },
      {
        date: '2025-01-20',
        provider: 'Asnan Tower Dental Center',
        provider_type: 'Dental Clinic',
        category: 'General Dentistry',
        type: 'Routine Cleaning',
        diagnosis: 'Dental hygiene maintenance',
        notes: 'Good oral health, next cleaning in 6 months',
        location: 'Kuwait City',
        rating: 4.7
      },
      {
        date: '2025-01-15',
        provider: 'Al Dawaa Pharmacy',
        provider_type: 'Pharmacy',
        category: 'Medication',
        type: 'Prescription Pickup',
        diagnosis: 'Diabetes medication refill',
        notes: 'Metformin 500mg x30 tablets dispensed',
        location: 'Salmiya',
        rating: 4.5
      },
      {
        date: '2025-01-10',
        provider: 'Gulf Medical Center',
        provider_type: 'Healthcare Clinic',
        category: 'Endocrinology',
        type: 'Consultation',
        diagnosis: 'Diabetes check-up',
        notes: 'HbA1c levels good, diet plan updated',
        location: 'Kuwait City',
        rating: 4.6
      },
      {
        date: '2025-01-05',
        provider: 'Serenity Wellness Spa',
        provider_type: 'Spa',
        category: 'Wellness',
        type: 'Stress Management',
        diagnosis: 'Relaxation therapy',
        notes: 'Deep tissue massage for stress relief',
        location: 'Avenues Mall',
        rating: 4.9
      },
      {
        date: '2024-12-28',
        provider: 'Glamour Beauty Salon',
        provider_type: 'Salon',
        category: 'Beauty',
        type: 'Hair & Beauty',
        diagnosis: 'Personal grooming',
        notes: 'Hair cut and color treatment',
        location: 'Hawally',
        rating: 4.4
      }
    ],
    
    // Upcoming appointments across different provider types
    upcoming_appointments: [
      {
        date: '2025-02-15',
        time: '10:00 AM',
        provider: 'Dr. Zahida Rani',
        provider_type: 'Doctor',
        category: 'Dermatology',
        type: 'Skin Consultation',
        location: 'Kuwait Dermatology Center',
        status: 'confirmed',
        notes: 'Annual skin check-up'
      },
      {
        date: '2025-02-20',
        time: '2:00 PM',
        provider: 'Diamond Dental Clinic',
        provider_type: 'Dental Clinic',
        category: 'Cosmetic Dentistry',
        type: 'Teeth Whitening',
        location: 'Salmiya',
        status: 'confirmed',
        notes: 'Professional whitening treatment'
      },
      {
        date: '2025-02-25',
        time: '11:00 AM',
        provider: 'Zen Spa & Wellness',
        provider_type: 'Spa',
        category: 'Wellness',
        type: 'Cupping Therapy',
        location: 'Kuwait City',
        status: 'pending',
        notes: 'Traditional cupping for muscle tension'
      }
    ],
    
    // Healthcare provider preferences
    preferred_providers: [
      {
        name: 'Dr. Sarah Al-Ahmad',
        type: 'Doctor',
        specialty: 'Cardiology',
        reason: 'Excellent user care and communication'
      },
      {
        name: 'Asnan Tower Dental Center',
        type: 'Dental Clinic',
        specialty: 'General Dentistry',
        reason: 'Professional staff and modern equipment'
      },
      {
        name: 'Al Dawaa Pharmacy',
        type: 'Pharmacy',
        specialty: 'Medication',
        reason: 'Convenient location and reliable service'
      }
    ]
  });

  // Check what information viewer can access
  const canViewField = (fieldName) => {
    const viewerRole = viewerPermissions?.role || 'public';
    
    switch (viewerRole) {
      case 'patient':
        return patientData.id === viewerPermissions?.userId; // Patient can only see own data
      case 'doctor':
      case 'authorized_doctor':
        return privacyLevels.PUBLIC.includes(fieldName) || 
               privacyLevels.MEDICAL_STAFF.includes(fieldName) ||
               privacyLevels.AUTHORIZED_DOCTOR.includes(fieldName);
      case 'medical_staff':
        return privacyLevels.PUBLIC.includes(fieldName) || 
               privacyLevels.MEDICAL_STAFF.includes(fieldName);
      default:
        return privacyLevels.PUBLIC.includes(fieldName);
    }
  };

  // Request access to protected information
  const requestAccess = (reason) => {
    setAccessRequest({
      requested_by: viewerPermissions?.userId,
      reason,
      requested_at: new Date().toISOString(),
      status: 'pending'
    });
    
    // In real app, this would send to backend for approval
    alert('Access request sent to user for approval');
  };

  // Privacy-aware field renderer
  const PrivacyField = ({ fieldName, children, requiredRole = 'medical_staff' }) => {
    if (canViewField(fieldName)) {
      return children;
    }
    
    return (
      <div className="bg-gray-100 rounded-lg p-4 text-center">
        <div className="text-gray-500 mb-2">
          <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <div className="font-medium">Protected Health Information</div>
          <div className="text-sm">Requires {requiredRole.replace('_', ' ')} access</div>
        </div>
        <button
          onClick={() => requestAccess(`Access to ${fieldName} for medical consultation`)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
        >
          Request Access
        </button>
      </div>
    );
  };

  // Render tabs based on permissions
  const availableTabs = [
    { id: 'overview', label: 'Overview', icon: '📋' },
    ...(canViewField('medical_history') ? [{ id: 'medical', label: 'Medical History', icon: '🏥' }] : []),
    ...(canViewField('lab_results') ? [{ id: 'labs', label: 'Lab Results', icon: '🧪' }] : []),
    { id: 'appointments', label: 'Appointments', icon: '📅' },
    ...(viewerPermissions?.role === 'patient' ? [{ id: 'settings', label: 'Privacy Settings', icon: '⚙️' }] : [])
  ];

  return (
    <>
      <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
            <div className="flex items-center space-x-4">
              <img
                src={patientData.profile_image}
                alt={patientData.name}
                className="w-16 h-16 rounded-full border-2 border-gray-200"
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{patientData.name}</h1>
                <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                  <span>{patientData.age} years old</span>
                  <span className="hidden sm:inline">•</span>
                  <span>{patientData.gender}</span>
                  <span className="hidden sm:inline">•</span>
                  <span className="hidden sm:inline">EnayaHub Healthcare Member</span>
                  <span className="hidden sm:inline">•</span>
                  <span className="text-xs">ID: {patientData.id}</span>
                </div>
              </div>
            </div>
            
            {/* Healthcare Platform Stats - Responsive */}
            <div className="flex-1 w-full md:px-4">
              <div className="grid grid-cols-3 gap-2 md:gap-4 text-center">
                <div className="bg-blue-50 rounded-lg p-2 md:p-3">
                  <div className="text-base md:text-lg font-bold text-blue-600">{patientData.recent_visits.length}</div>
                  <div className="text-xs text-blue-700">Recent Visits</div>
                </div>
                <div className="bg-green-50 rounded-lg p-2 md:p-3">
                  <div className="text-base md:text-lg font-bold text-green-600">{patientData.upcoming_appointments.length}</div>
                  <div className="text-xs text-green-700">Upcoming</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-2 md:p-3">
                  <div className="text-base md:text-lg font-bold text-purple-600">{patientData.preferred_providers.length}</div>
                  <div className="text-xs text-purple-700">Preferred</div>
                </div>
              </div>
            </div>
            
            {/* Privacy & Platform Indicator - Fixed for Mobile */}
            <div className="hidden md:block ml-auto space-y-2">
              <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                🔒 Privacy Protected
              </div>
              <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                🏥 EnayaHub Healthcare
              </div>
            </div>
          </div>
          
          {/* Mobile Privacy Indicators */}
          <div className="md:hidden mt-4 flex justify-center space-x-2">
            <div className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
              🔒 Privacy Protected
            </div>
            <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
              🏥 EnayaHub Healthcare
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs - Mobile Responsive */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex overflow-x-auto space-x-4 md:space-x-8">
            {availableTabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 md:px-1 border-b-2 font-medium text-sm flex items-center space-x-2 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="text-xs md:text-sm">{tab.icon}</span>
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Emergency Information */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Emergency Information</h2>
                
                <PrivacyField fieldName="emergency_contact" requiredRole="medical_staff">
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Emergency Contact</label>
                      <div className="text-gray-900">{patientData.emergency_contact.name}</div>
                      <div className="text-gray-600">{patientData.emergency_contact.phone}</div>
                    </div>
                  </div>
                </PrivacyField>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Allergies & Medications</h2>
                
                <PrivacyField fieldName="allergies" requiredRole="medical_staff">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-2">Known Allergies</label>
                      <div className="flex flex-wrap gap-2">
                        {patientData.allergies.map((allergy, index) => (
                          <span key={index} className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                            ⚠️ {allergy}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-2">Current Medications</label>
                      <div className="space-y-2">
                        {patientData.current_medications.map((med, index) => (
                          <div key={index} className="bg-blue-50 rounded-lg p-3">
                            <div className="font-medium text-gray-900">{med.name}</div>
                            <div className="text-sm text-gray-600">{med.frequency}</div>
                            <div className="text-sm text-blue-600">Prescribed by {med.prescribed_by}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </PrivacyField>
              </div>

              {/* Insurance Information */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Insurance Information</h2>
                
                <PrivacyField fieldName="insurance_info" requiredRole="patient_only">
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700 block mb-1">Insurance Company</label>
                        <div className="text-gray-900 font-medium">{patientData.insurance_info.insurance_company}</div>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-gray-700 block mb-1">Policy Holder Name</label>
                        <div className="text-gray-900">{patientData.insurance_info.policy_holder_name}</div>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-gray-700 block mb-1">Insurance Number</label>
                        <div className="text-gray-900 font-mono">{patientData.insurance_info.insurance_number}</div>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-gray-700 block mb-1">Group Number</label>
                        <div className="text-gray-900 font-mono">{patientData.insurance_info.group_number}</div>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-gray-700 block mb-1">Issuance Date</label>
                        <div className="text-gray-900">{new Date(patientData.insurance_info.issuance_date).toLocaleDateString()}</div>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-gray-700 block mb-1">Expiration Date</label>
                        <div className="text-gray-900">{new Date(patientData.insurance_info.expiration_date).toLocaleDateString()}</div>
                      </div>
                    </div>
                    
                    <div className="border-t pt-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700 block mb-1">Copay Amount</label>
                        <div className="text-gray-900 font-medium">{patientData.insurance_info.copay}</div>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <button 
                        onClick={handleEditInsurance}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-2 hover:bg-blue-50 px-3 py-2 rounded-lg transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit Insurance Information
                      </button>
                    </div>
                  </div>
                </PrivacyField>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Recent Healthcare Activities */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <span className="mr-2">🏥</span>
                  Recent Healthcare Activities
                </h3>
                
                <div className="space-y-4">
                  {patientData.recent_visits.map((visit, index) => (
                    <div key={index} className="border-l-4 border-blue-200 pl-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-1">
                            <span className="text-sm font-medium text-gray-900">{visit.provider}</span>
                            <span className={`ml-2 px-2 py-1 text-xs rounded-full font-medium ${
                              visit.provider_type === 'Doctor' ? 'bg-blue-100 text-blue-700' :
                              visit.provider_type === 'Healthcare Clinic' ? 'bg-emerald-100 text-emerald-700' :
                              visit.provider_type === 'Dental Clinic' ? 'bg-green-100 text-green-700' :
                              visit.provider_type === 'Pharmacy' ? 'bg-orange-100 text-orange-700' :
                              visit.provider_type === 'Spa' ? 'bg-teal-100 text-teal-700' :
                              visit.provider_type === 'Salon' ? 'bg-pink-100 text-pink-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {visit.provider_type}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">{visit.category} • {visit.type}</div>
                          <div className="text-xs text-gray-500 mt-1">
                            {new Date(visit.date).toLocaleDateString()} • {visit.location}
                          </div>
                          {visit.notes && (
                            <div className="text-xs text-gray-600 mt-2 italic">
                              "{visit.notes}"
                            </div>
                          )}
                        </div>
                        <div className="flex items-center text-xs text-yellow-600 ml-2">
                          ⭐ {visit.rating}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Upcoming Appointments - Enhanced */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <span className="mr-2">📅</span>
                  Upcoming Appointments
                </h3>
                
                <div className="space-y-3">
                  {patientData.upcoming_appointments.map((apt, index) => (
                    <div key={index} className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center mb-1">
                            <span className="font-medium text-gray-900">{apt.provider}</span>
                            <span className={`ml-2 px-2 py-1 text-xs rounded-full font-medium ${
                              apt.provider_type === 'Doctor' ? 'bg-blue-100 text-blue-700' :
                              apt.provider_type === 'Healthcare Clinic' ? 'bg-emerald-100 text-emerald-700' :
                              apt.provider_type === 'Dental Clinic' ? 'bg-green-100 text-green-700' :
                              apt.provider_type === 'Spa' ? 'bg-teal-100 text-teal-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {apt.provider_type}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">{apt.category} • {apt.type}</div>
                          <div className="text-sm text-green-600 mt-1">
                            {new Date(apt.date).toLocaleDateString()} at {apt.time}
                          </div>
                          <div className="text-xs text-gray-500">
                            📍 {apt.location}
                          </div>
                          {apt.notes && (
                            <div className="text-xs text-gray-600 mt-2 italic">
                              "{apt.notes}"
                            </div>
                          )}
                        </div>
                        <div className={`px-2 py-1 text-xs rounded-full font-medium ${
                          apt.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {apt.status}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preferred Providers */}
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <span className="mr-2">⭐</span>
                  Preferred Providers
                </h3>
                
                <div className="space-y-3">
                  {patientData.preferred_providers.map((provider, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center mb-1">
                        <span className="font-medium text-gray-900">{provider.name}</span>
                        <span className={`ml-2 px-2 py-1 text-xs rounded-full font-medium ${
                          provider.type === 'Doctor' ? 'bg-blue-100 text-blue-700' :
                          provider.type === 'Dental Clinic' ? 'bg-green-100 text-green-700' :
                          provider.type === 'Pharmacy' ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {provider.type}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">{provider.specialty}</div>
                      <div className="text-xs text-gray-500 mt-1 italic">
                        "{provider.reason}"
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'medical' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Medical History</h2>
            
            <PrivacyField fieldName="medical_history" requiredRole="authorized_doctor">
              <div className="space-y-6">
                {patientData.medical_history.map((condition, index) => (
                  <div key={index} className="border-l-4 border-yellow-400 pl-6 pb-6">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{condition.condition}</h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        condition.status === 'Managed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                      }`}>
                        {condition.status}
                      </span>
                    </div>
                    <div className="text-gray-600">
                      <div>Diagnosed: {new Date(condition.diagnosed).toLocaleDateString()}</div>
                      <div>Treating Doctor: {condition.treating_doctor}</div>
                    </div>
                  </div>
                ))}
              </div>
            </PrivacyField>
          </div>
        )}

        {activeTab === 'labs' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Laboratory Results</h2>
            
            <PrivacyField fieldName="lab_results" requiredRole="authorized_doctor">
              <div className="space-y-4">
                {patientData.lab_results.map((result, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-900">{result.test}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        result.status === 'Normal' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {result.status}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <div className="text-gray-500">Result</div>
                        <div className="font-medium">{result.result}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Normal Range</div>
                        <div className="font-medium">{result.normal_range}</div>
                      </div>
                      <div>
                        <div className="text-gray-500">Date</div>
                        <div className="font-medium">{new Date(result.date).toLocaleDateString()}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </PrivacyField>
          </div>
        )}

        {/* Access Request Notification */}
        {accessRequest && (
          <div className="fixed bottom-4 right-4 bg-blue-600 text-white p-4 rounded-lg shadow-lg max-w-sm">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <div className="font-medium">Access Request Sent</div>
                <div className="text-sm opacity-90">User will be notified</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>

    {/* Insurance Information Modal */}
    <InsuranceInfoModal
      isOpen={showInsuranceModal}
      onClose={() => setShowInsuranceModal(false)}
      onSave={saveInsuranceInfo}
      initialData={insuranceData || patientData.insurance_info}
    />
    </>
  );
};

export default PatientProfile;